import { Progress } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { featureCollection } from '@turf/helpers';
import dayjs from 'dayjs';
import { MapEvent } from 'mapbox-gl';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl';

import { useQueriesWithGlobal } from '@data-access';
import {
  FieldsFeatureCollection,
  fieldsQueries,
  type MapPointScope,
  selectPlotmapFeatures,
  serializeExtentBbox,
} from '@fields/data-access';
import {
  PlotsLayer,
  ClusterLayer,
  SatelliteImageryControl,
  ImageLayer,
} from '@map';

export const FIELDS_LAYER_ID = 'fields-layer';
export const CLUSTER_LAYER_ID = 'cluster-layer';

export const INTERACTIVE_LAYER_IDS = [
  FIELDS_LAYER_ID,
  CLUSTER_LAYER_ID,
  `${CLUSTER_LAYER_ID}_points`,
];

interface FieldsInBoundsProps {
  fieldId?: string;
  withSatelliteControl?: boolean;
  hideFieldsLayer?: boolean;
  hideClusters?: boolean;
  /** Full extent of assigned ilots — plotmap is fetched once for the session. */
  plotmapBbox?: [number, number, number, number] | null;
  scope?: MapPointScope;
}

export function FieldsInBounds({
  fieldId,
  withSatelliteControl = true,
  hideFieldsLayer = false,
  hideClusters = false,
  plotmapBbox = null,
  scope = 'personal',
}: FieldsInBoundsProps) {
  const { data, query, heatmaps, heatmapsLayerState, setHeatmapsLayerState } =
    useFieldsInBounds({
      filterIds: fieldId ? [fieldId] : [],
      plotmapBbox,
      scope,
    });

  return (
    <>
      {query.data?.datelist && withSatelliteControl ? (
        <SatelliteImageryControl
          date={
            heatmapsLayerState.date
              ? dayjs(heatmapsLayerState.date).toDate()
              : null
          }
          dates={query.data.datelist}
          imageType={heatmapsLayerState.imageType}
          loading={heatmaps.isFetching}
          onDateChange={(date) =>
            setHeatmapsLayerState({
              ...heatmapsLayerState,
              date: dayjs(date).format('YYYY-MM-DD'),
            })
          }
          onEnableChange={(enabled) =>
            setHeatmapsLayerState({ ...heatmapsLayerState, enabled })
          }
          onImageTypeChange={(imageType) =>
            setHeatmapsLayerState({ ...heatmapsLayerState, imageType })
          }
        />
      ) : null}

      {query.isFetching && !query.data ? (
        <Progress
          animated
          opacity={0.9}
          radius={0}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            zIndex: 1,
          }}
          value={100}
        />
      ) : null}

      {heatmapsLayerState.enabled
        ? heatmaps.data?.map(({ heatmaps: heatmap }) => (
            <Fragment key={heatmap.data[0].url}>
              <ImageLayer
                beforeId="fields-outline-heatmap_outline"
                coordinates={heatmap.corners_coords}
                imageUrl={heatmap.data[0].url}
              />
              <PlotsLayer
                data={data}
                id="fields-outline-heatmap"
                minZoom={12}
                outline
              />
            </Fragment>
          ))
        : null}

      {!heatmapsLayerState.enabled && !hideFieldsLayer ? (
        <PlotsLayer
          data={data}
          id={FIELDS_LAYER_ID}
          minZoom={0}
          withLabel={false}
        />
      ) : null}

      {!hideClusters ? (
        <ClusterLayer
          clusterColor="#ED7D31"
          data={data as any}
          layerId={CLUSTER_LAYER_ID}
          pointsMaxZoom={12}
        />
      ) : null}
    </>
  );
}

interface UseFieldsInBoundsParams {
  filterIds?: string[];
  plotmapBbox?: [number, number, number, number] | null;
  scope?: MapPointScope;
}

export function useFieldsInBounds({
  filterIds,
  plotmapBbox = null,
  scope = 'personal',
}: UseFieldsInBoundsParams = {}) {
  const { current: map } = useMap();
  const [mapZoom, setMapZoom] = useState(0);
  const lastRenderedRef = useRef<FieldsFeatureCollection>(
    featureCollection([]) as FieldsFeatureCollection,
  );
  const [heatmapsLayerState, setHeatmapsLayerState] = useState({
    enabled: false,
    date: '',
    imageType: 'NDVI',
  });

  const fetchBboxKey = useMemo(
    () => (plotmapBbox ? serializeExtentBbox(plotmapBbox) : null),
    [plotmapBbox],
  );

  const cluster = mapZoom < 12;
  const queries = useQueriesWithGlobal(fieldsQueries);

  const query = useQuery({
    ...queries.boundedList({
      bbox: fetchBboxKey!,
      cluster,
      zoom: mapZoom,
      scope,
    }),
    enabled: !!fetchBboxKey,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const heatmaps = useQuery({
    ...queries.boundedHeatmaps({
      bbox: fetchBboxKey!,
      layer: heatmapsLayerState.imageType,
      date: heatmapsLayerState.date,
    }),
    enabled:
      !cluster &&
      !!query.data?.datelist?.length &&
      !!heatmapsLayerState.date &&
      heatmapsLayerState.enabled &&
      !!fetchBboxKey,
  });

  const fieldsInBounds = useMemo(() => {
    const next = selectPlotmapFeatures({
      source: query.data,
      filterIds,
      hideFilteredIds: !heatmapsLayerState.enabled,
      lastRendered: lastRenderedRef.current,
    });

    lastRenderedRef.current = next;
    return next;
  }, [filterIds, heatmapsLayerState.enabled, query.data]);

  const handleMapIdle = useCallback((e: MapEvent) => {
    const nextZoom = e.target.getZoom();
    setMapZoom((currentZoom) =>
      Math.abs(currentZoom - nextZoom) < 0.01 ? currentZoom : nextZoom,
    );
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    map.on('idle', handleMapIdle);

    return () => {
      map.off('idle', handleMapIdle);
    };
  }, [map, handleMapIdle]);

  useEffect(() => {
    if (query.data?.datelist?.length) {
      const lastDate = query.data.datelist[query.data.datelist.length - 1];
      setHeatmapsLayerState((state) => ({
        ...state,
        date: lastDate,
      }));
    }
  }, [query.data?.datelist]);

  return {
    handleMapIdle,
    query,
    heatmaps,
    heatmapsLayerState,
    setHeatmapsLayerState,
    data: fieldsInBounds,
    interactiveLayerIds: INTERACTIVE_LAYER_IDS,
  };
}

export default FieldsInBounds;
